import { useState } from 'react'

// Demo Walkthrough Modal — short version. Each step is a single
// "what to show + what to say" block instead of the previous
// what / why / say / try four-section breakdown which was too long
// to scroll through during a real demo. Built for two audiences:
// the operator running a sales demo, and a new customer
// self-exploring after they hit "Load demo data."
export default function DemoWalkthroughModal({ onClose }) {
  const [step, setStep] = useState(0)
  const total = STEPS.length
  const cur = STEPS[step]

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-2 border-b border-black/5 px-5 py-3">
          <div>
            <div className="text-[10px] font-medium text-blue-700">
              Demo guide · {step + 1}/{total}
            </div>
            <h2 className="text-base font-medium leading-tight">{cur.title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-black/55 hover:bg-black/5"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="text-xs font-medium text-blue-700">
            {cur.tab}
          </div>
          <p className="mt-2 text-sm text-black/85 leading-snug">{cur.show}</p>
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div className="text-[10px] font-medium text-emerald-700">
              Say this
            </div>
            <p className="mt-0.5 text-sm italic text-emerald-900 leading-snug">"{cur.say}"</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-black/5 px-5 py-3">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="rounded-lg border border-black/15 px-3 py-1.5 text-sm font-medium text-black/65 hover:bg-black/5 disabled:opacity-40"
          >
            ←
          </button>
          <div className="flex items-center gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-4 rounded-full ${
                  i === step ? 'bg-blue-600' : i < step ? 'bg-blue-200' : 'bg-black/10'
                }`}
              />
            ))}
          </div>
          {step < total - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Each step is intentionally short — one short paragraph + one
// pasteable line. The whole modal should fit on a phone screen
// without scrolling.
const STEPS = [
  {
    title: 'Daily Huddle',
    tab: 'Huddle',
    show: 'AI Morning Brief at the top — every bullet is tied to a dollar figure. Below: low items, social drafts ready, today\'s waste, lifetime savings.',
    say: 'Five minutes every morning. The AI tells the manager what to act on — not just shows them numbers.',
  },
  {
    title: 'Receive — invoice scanning',
    tab: 'Stock → Receive',
    show: 'Snap a photo of any invoice and the app extracts the line items. Tap "Check for anomalies" to flag price hikes — notice the brisket went from $9.50 to $9.65.',
    say: 'No more typing line items off paper invoices. Snap, review, save.',
  },
  {
    title: 'Reorder — what to buy',
    tab: 'Stock → Reorder',
    show: 'Items below par are flagged. AI Suggest writes a shopping list based on stock, sales velocity, and supplier patterns.',
    say: 'No clipboard walks through the cooler. The AI tells you what you need.',
  },
  {
    title: 'Variance — finding leaks',
    tab: 'Stock → Variance',
    show: 'What recipes should have used vs. what actually moved out. The gap is shrinkage, theft, or over-portioning.',
    say: 'A 1% variance on $40k of food cost is $400/month. Most restaurants don\'t track this at all.',
  },
  {
    title: 'Schedule + Insights',
    tab: 'Schedule / Insights',
    show: 'Schedule shows the week\'s shifts and labor cost. Insights gives weekly sales / labor / waste / prime cost with prior-week comparison, plus the 7-day forecast and menu engineering matrix.',
    say: 'Weekly review meeting in 2 minutes instead of 30. Stars / Plowhorses / Puzzles / Dogs — done for you.',
  },
  {
    title: 'Marketing — drafts ready',
    tab: 'Marketing',
    show: '3 demo promos (running, planned, past) and 10 AI-generated drafts ready to review. Tap "Generate posts" on any promo to see Instagram + Facebook + SMS copy in seconds.',
    say: 'A week of social content in 30 seconds. Don\'t want to post yourself? +$149/mo and we handle it.',
  },
  {
    title: 'Settings — compliance',
    tab: 'Settings',
    show: 'Liquor license expires in 45 days — yellow warning fires automatically. Plus food handler cert, business license, health permit, insurance.',
    say: 'A lapsed permit costs real money. The app reminds you 90 days out so it never happens.',
  },
  {
    title: 'The whole loop',
    tab: 'Anywhere',
    show: 'Morning: read the brief, act on flags. Day: snap invoices, log waste in 5 seconds. Week: review Insights, approve schedule + marketing.',
    say: 'Five hours a week back. Less guessing, more ownership. Pays for itself many times over.',
  },
]
